export const romCacheName = 'retro-arcade-rom-cache-v1';

export interface RomCacheIdentity {
  access: 'PUBLIC' | 'PRIVATE_TEST';
  catalogRevision: number;
  runtimeId: string;
  sha256: string;
  sizeBytes: number;
}

export interface LoadRomBinaryInput extends RomCacheIdentity {
  url: string;
}

export interface LoadedRomBinary {
  blob: Blob;
  source: 'cache' | 'network';
  cacheKey: string | null;
}

export interface RomCacheSummary {
  entryCount: number;
  totalBytes: number;
}

const cachePathPrefix = '/__retro-arcade-cache__/rom/';

function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

export function buildRomCacheKey(origin: string, identity: RomCacheIdentity): string {
  const normalizedOrigin = origin.replace(/\/$/, '');
  return `${normalizedOrigin}${cachePathPrefix}${identity.catalogRevision}/${encodeSegment(
    identity.runtimeId,
  )}/${identity.sha256}`;
}

export function isCacheEntryCurrentRevision(url: string, revision: number): boolean {
  try {
    const parsed = new URL(url);
    return parsed.pathname.startsWith(`${cachePathPrefix}${revision}/`);
  } catch {
    return false;
  }
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', buffer));
  return Array.from(digest)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function fetchAndVerifyBinary(input: LoadRomBinaryInput): Promise<ArrayBuffer> {
  const response = await fetch(input.url, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  });

  if (!response.ok) {
    throw new Error(`ROM_DOWNLOAD_FAILED: HTTP ${response.status}`);
  }

  const buffer = await response.arrayBuffer();
  if (buffer.byteLength !== input.sizeBytes) {
    throw new Error('ROM_SIZE_MISMATCH');
  }

  const digest = await sha256Hex(buffer);
  if (digest !== input.sha256) {
    throw new Error('ROM_SHA256_MISMATCH');
  }

  return buffer;
}

export function summarizeRomCacheSizes(sizes: readonly number[]): RomCacheSummary {
  const normalized = sizes.filter((size) => Number.isFinite(size) && size > 0);
  return {
    entryCount: sizes.length,
    totalBytes: normalized.reduce((total, size) => total + size, 0),
  };
}

export async function inspectRomCache(): Promise<RomCacheSummary> {
  if (typeof caches === 'undefined') return { entryCount: 0, totalBytes: 0 };

  try {
    const cacheNames = await caches.keys();
    if (!cacheNames.includes(romCacheName)) return { entryCount: 0, totalBytes: 0 };

    const cache = await caches.open(romCacheName);
    const requests = await cache.keys();
    const responses = await Promise.all(requests.map((request) => cache.match(request)));
    const sizes = responses.map((response) =>
      Number(response?.headers.get('x-retro-arcade-size-bytes') ?? 0),
    );
    return summarizeRomCacheSizes(sizes);
  } catch {
    return { entryCount: 0, totalBytes: 0 };
  }
}

export async function clearRomCache(): Promise<boolean> {
  if (typeof caches === 'undefined') return false;

  try {
    return await caches.delete(romCacheName);
  } catch {
    return false;
  }
}

async function pruneOldRevisions(cache: Cache, revision: number): Promise<void> {
  const requests = await cache.keys();
  await Promise.all(
    requests
      .filter((request) => !isCacheEntryCurrentRevision(request.url, revision))
      .map((request) => cache.delete(request)),
  );
}

function cachedResponseMatches(response: Response, input: LoadRomBinaryInput): boolean {
  return (
    response.headers.get('x-retro-arcade-sha256') === input.sha256 &&
    response.headers.get('x-retro-arcade-runtime-id') === input.runtimeId &&
    response.headers.get('x-retro-arcade-catalog-revision') === String(input.catalogRevision) &&
    Number(response.headers.get('x-retro-arcade-size-bytes')) === input.sizeBytes
  );
}

export async function loadRomBinary(
  input: LoadRomBinaryInput,
  origin = window.location.origin,
): Promise<LoadedRomBinary> {
  if (input.access === 'PRIVATE_TEST' || typeof caches === 'undefined') {
    const buffer = await fetchAndVerifyBinary(input);
    return {
      blob: new Blob([buffer], { type: 'application/octet-stream' }),
      source: 'network',
      cacheKey: null,
    };
  }

  const cacheKey = buildRomCacheKey(origin, input);
  const request = new Request(cacheKey, { method: 'GET' });
  let cache: Cache | null;

  try {
    cache = await caches.open(romCacheName);
    await pruneOldRevisions(cache, input.catalogRevision);

    const cached = await cache.match(request);
    if (cached && cachedResponseMatches(cached, input)) {
      const cachedBlob = await cached.blob();
      const cachedDigest = await sha256Hex(await cachedBlob.arrayBuffer());
      if (cachedBlob.size === input.sizeBytes && cachedDigest === input.sha256) {
        return {
          blob: cachedBlob,
          source: 'cache',
          cacheKey,
        };
      }
    }

    if (cached) await cache.delete(request);
  } catch {
    cache = null;
  }

  const buffer = await fetchAndVerifyBinary(input);

  if (cache) {
    const headers = new Headers({
      'content-type': 'application/octet-stream',
      'x-retro-arcade-size-bytes': String(buffer.byteLength),
      'x-retro-arcade-catalog-revision': String(input.catalogRevision),
      'x-retro-arcade-runtime-id': input.runtimeId,
      'x-retro-arcade-sha256': input.sha256,
    });

    try {
      await cache.put(request, new Response(buffer.slice(0), { status: 200, headers }));
    } catch {
      // Cache Storage is an optimization only; a verified network binary remains launchable.
    }
  }

  return {
    blob: new Blob([buffer], { type: 'application/octet-stream' }),
    source: 'network',
    cacheKey,
  };
}
